import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./BuyNbundle.module.css";
import BundlePreviewWrapper, {
    CustomSwatches,
    Swatches,
    VariantSelector,
    VariantSelectorV2,
} from "../../BundlePreviewWrapper/BundlePreviewWrapper";
import { ProductObj, BuyNConfigInterface, VariantsObj } from "../../Types";
import {
    getPriceLabel,
    getFinalPrice,
    getDiscountValueWithSign,
    fetchCollectionProducts,
    buildMetafieldFromSwatchOptions,
    fetchStoreProducts,
} from "../../constants";
import { TStoreContext } from "../../../../context/types";
import ProductImageWrapper from "../../BundlePreviewWrapper/ProductImageWrapper";
import useIsMobile from "../../../../hooks/useIsMobile";
import Spinner from "../../../../components/Icons/Spinner";
import { InspectorEditableBlock } from "../../CommonFields/InspectorEditableBlock";
import { getProductId } from "../../../constants";
import { getDiscountValue } from "../../../utilities";

const TickIconSvg = ({ color }: { color: string }) => (
    <svg
        width="20px"
        height="20px"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ width: 20, height: 20 }}
    >
        <path
            d="M16.0303 10.0303C16.3232 9.73744 16.3232 9.26256 16.0303 8.96967C15.7374 8.67678 15.2626 8.67678 14.9697 8.96967L10.5 13.4393L9.03033 11.9697C8.73744 11.6768 8.26256 11.6768 7.96967 11.9697C7.67678 12.2626 7.67678 12.7374 7.96967 13.0303L9.96967 15.0303C10.2626 15.3232 10.7374 15.3232 11.0303 15.0303L16.0303 10.0303Z"
            fill={color}
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12 1.25C6.06294 1.25 1.25 6.06294 1.25 12C1.25 17.9371 6.06294 22.75 12 22.75C17.9371 22.75 22.75 17.9371 22.75 12C22.75 6.06294 17.9371 1.25 12 1.25ZM2.75 12C2.75 6.89137 6.89137 2.75 12 2.75C17.1086 2.75 21.25 6.89137 21.25 12C21.25 17.1086 17.1086 21.25 12 21.25C6.89137 21.25 2.75 17.1086 2.75 12Z"
            fill={color}
        />
    </svg>
);

const DottedCircle = ({ color }: { color: string }) => (
    <svg
        width="20px"
        height="20px"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ width: 20, height: 20 }}
    >
        <path
            d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="4 4"
        />
    </svg>
);

const BuyNbundle = (bundleConfig: BuyNConfigInterface) => {
    return (
        !!bundleConfig && (
            <BuyNPreview
                bundleConfig={bundleConfig}
                standAlone={true}
                appConfigContext={
                    {
                        currency: window.kefi_bundles.localization.currency,
                        money_format: window.kefi_bundles.money_format,
                        sft: window.kefi_bundles.sft,
                    } as TStoreContext
                }
            />
        )
    );
};

type Props_Buy_N_Preview = {
    bundleConfig: BuyNConfigInterface;
    standAlone?: boolean;
    appConfigContext: TStoreContext;
};

const ProductCardShimmer = ({ type }: { type: "collection" | "specific" }) => {
    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100px",
                fontSize: "20px",
                color: "#999999",
            }}
        >
            {type === "collection" ? "Select Collection" : "Select product(s)"}
        </div>
    );
};

const ProductCard = ({
    product,
    index,
    bundleConfig,
    appConfigContext,
    standAlone = false,
    setTotalQuantities,
    discountType,
    discountValue,
    onVariantChange,
    setUnavailableSelected,
    finalProductsList,
    setFinalProductsList,
    setTotalPrice,
    isMobile,
    type = "product",
    discountedVariantsArray,
}: {
    product: ProductObj;
    index: number;
    bundleConfig: BuyNConfigInterface;
    appConfigContext: TStoreContext;
    standAlone: boolean;
    setTotalQuantities: React.Dispatch<React.SetStateAction<number>>;
    discountType: "percentage" | "flat" | "free" | "fixed";
    discountValue: number;
    onVariantChange: (index: number, variant: VariantsObj) => void;
    unavailableSelected: number[];
    setUnavailableSelected: React.Dispatch<
        React.SetStateAction<{
            [key: number]: number[];
        }>
    >;
    finalProductsList: any;
    setFinalProductsList: any;
    setTotalPrice: any;
    isMobile: boolean;
    type?: string;
    discountedVariantsArray: any[];
}) => {
    const [selectedVariants, setSelectedVariants] = useState<VariantsObj[]>([]);
    const { currency, money_format } = appConfigContext;

    // Update selected variant and prices when variant changes
    useEffect(() => {
        product.selectedVariants = selectedVariants;
        onVariantChange(index, selectedVariants[selectedVariants.length - 1]);

        // if there is change in selected variant, update finalProductsList too
        setFinalProductsList((prev: any) => {
            const updatedList = prev
                .map((_product: any) => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { images, options, variants, ...restt } = _product;

                    if (_product.id == product.id) {
                        return { ...restt, selectedVariants: selectedVariants };
                    }
                    return _product;
                })
                .filter((__product: any) => __product?.selectedVariants?.length !== 0); // Remove null products (those that have no variants left)
            return updatedList;
        });
    }, [selectedVariants, index, product]);

    useEffect(() => {
        if (type === "collection") {
            if (bundleConfig?.collections?.length == 0) {
                setSelectedVariants([]);
                setFinalProductsList([]);
            }
            return;
        }
        setSelectedVariants([]);
        setFinalProductsList([]);
    }, [product, bundleConfig?.collections]);

    // Handle product click for standalone or Shopify links
    const handleProductClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (standAlone && !window.location.pathname.includes(product.handle)) {
            const productURL = `https://${window.location.hostname}/products/${product.handle}`;
            window.open(productURL, "_blank");
        } else if (!window.location.pathname.includes(product.handle)) {
            const productURL = `https://${shopify.config.shop}/products/${product.handle}`;
            window.open(productURL, "_blank");
        }
    };
    const updateQuantity = (change: number) => {
        let updatedSv: VariantsObj[] = [];
        setSelectedVariants((prevSv) => {
            // selectedVariants updation
            updatedSv = [...prevSv];
            if (change == +1) {
                updatedSv.push(updatedSv[updatedSv?.length - 1]);
            } else {
                updatedSv.pop();
            }
            setFinalProductsList((prev: any) => {
                const updatedList = prev
                    .map((_product: any) => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const { images, options, variants, ...restt } = _product;

                        if (_product.id == product.id) {
                            return { ...restt, selectedVariants: updatedSv };
                        }
                        return _product;
                    })
                    .filter((__product: any) => __product?.selectedVariants?.length !== 0); // Remove null products (those that have no variants left)
                return updatedList;
            });
            return updatedSv;
        });
    };

    useEffect(() => {
        // Calculate total quantity
        if (finalProductsList.length === 0) {
            setTotalQuantities(0);
            setTotalPrice(0);
        } else {
            let tQty = 0;
            let tPrice = 0;
            finalProductsList.forEach((fpl: any) => {
                tQty += fpl?.selectedVariants?.length || 0;
                fpl.selectedVariants?.length &&
                    fpl.selectedVariants.forEach((sv: any) => {
                        tPrice = tPrice + (+sv?.price || 0);
                    });
            });
            setTotalPrice(+tPrice);
            setTotalQuantities(tQty);
        }
    }, [finalProductsList]);

    // const renderPrice = (
    //     <div
    //         className={styles.productMetaRight}
    //         style={{
    //             color: bundleConfig?.bundles_design.highlighted_price_tag_color,
    //             fontSize:
    //                 bundleConfig?.bundles_design.highlighted_price_tag_size < 50
    //                     ? bundleConfig?.bundles_design.highlighted_price_tag_size
    //                     : 50,
    //             fontWeight: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
    //             fontStyle: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
    //         }}
    //     >
    //         {product?.selectedVariants?.length > 0 && (
    //             <div
    //                 style={{
    //                     width: "max-content",
    //                 }}
    //             >
    //                 {getPriceLabel(
    //                     getFinalPrice(
    //                         "none",
    //                         0,
    //                         product.selectedVariants.reduce((acc, s) => acc + +s.price, 0),
    //                     ),
    //                     money_format,
    //                     currency,
    //                 )}
    //             </div>
    //         )}
    //     </div>
    // );
    const renderPrice = () => {
        const productVariants = [...(product?.selectedVariants || [])];

        const totalOriginalPrice = productVariants.reduce((acc, variant) => acc + parseFloat(variant.price), 0);

        // Sort variants the same way as calculateTotalPrices
        if (bundleConfig?.discount_option_type === "cheapest") {
            productVariants.sort((a: any, b: any) => parseFloat(b.price) - parseFloat(a.price));
        } else if (bundleConfig?.discount_option_type === "expensive") {
            productVariants.sort((a: any, b: any) => parseFloat(a.price) - parseFloat(b.price));
        }

        // Find which of this product's variants are in the discounted set (consume one-by-one to handle quantities)
        const remainingDiscounted = [...discountedVariantsArray];
        const discountedProductVariants: any[] = [];
        productVariants.forEach((v: any) => {
            const idx = remainingDiscounted.findIndex((dv: any) => dv.id === v.id);
            if (idx > -1) {
                discountedProductVariants.push(v);
                remainingDiscounted.splice(idx, 1);
            }
        });

        let totalDiscount = 0;

        if (discountedProductVariants.length > 0) {
            const discountValueObj = getDiscountValue(discountType, discountValue, discountedProductVariants);

            let targetTotal = 0;
            discountedProductVariants.forEach((v: any) => {
                targetTotal += parseFloat(v.price);
            });

            if (discountValueObj?.percentage) {
                totalDiscount += (targetTotal * discountValueObj.percentage.value) / 100;
            }

            if (discountValueObj?.fixedAmount) {
                totalDiscount += discountValueObj.fixedAmount.amount;
            }
        }

        let totalDiscountedPrice = totalOriginalPrice - totalDiscount;
        if (totalDiscountedPrice < 0) totalDiscountedPrice = 0;

        const showFreeText = totalDiscountedPrice === 0;
        const showStrikethrough = totalOriginalPrice !== totalDiscountedPrice;

        return (
            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productPrice">
                <div className={styles.productMetaRight}>
                    {product?.selectedVariants?.length > 0 && (
                        <div className={styles.priceContainer}>
                            {showStrikethrough && (
                                <span
                                    style={{
                                        textDecoration: "line-through",
                                        color: bundleConfig?.bundles_design.product_price_color,
                                        fontSize:
                                            bundleConfig?.bundles_design.product_price_size < 50
                                                ? bundleConfig?.bundles_design.product_price_size
                                                : 50,
                                        fontWeight: bundleConfig?.bundles_design.product_price_style?.split(" ")[0],
                                        fontStyle: bundleConfig?.bundles_design.product_price_style?.split(" ")[1],
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {getPriceLabel(
                                        getFinalPrice("none", 0, totalOriginalPrice),
                                        money_format,
                                        currency,
                                    )}
                                </span>
                            )}

                            {showFreeText ? (
                                <span
                                    style={{
                                        color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                                        fontSize:
                                            bundleConfig?.bundles_design.highlighted_price_tag_size < 50
                                                ? bundleConfig?.bundles_design.highlighted_price_tag_size
                                                : 50,
                                        fontWeight:
                                            bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                                        fontStyle:
                                            bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                                    }}
                                >
                                    {bundleConfig?.free_text || "Free"}
                                </span>
                            ) : (
                                <span
                                    style={{
                                        color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                                        fontSize:
                                            bundleConfig?.bundles_design.highlighted_price_tag_size < 50
                                                ? bundleConfig?.bundles_design.highlighted_price_tag_size
                                                : 50,
                                        fontWeight:
                                            bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                                        fontStyle:
                                            bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {getPriceLabel(
                                        getFinalPrice("none", 0, totalDiscountedPrice),
                                        money_format,
                                        currency,
                                    )}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </InspectorEditableBlock>
        );
    };

    const renderAddCta = (
        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="addButton">
            <button
                style={{
                    padding: "4px 16px",
                    cursor: "pointer",
                    backgroundColor: bundleConfig?.bundles_design?.add_to_cart_bg_color,
                    color: bundleConfig?.bundles_design?.add_to_cart_text_color,
                    borderRadius: bundleConfig?.bundles_design?.add_to_cart_border_radius,
                    border: `1px solid ${bundleConfig?.bundles_design?.add_to_cart_border_color}`,
                    lineHeight: "normal",
                }}
                onClick={() => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    let curSub: any = {};
                    if (product?.subscriptions?.length > 0 && product.subscriptions[0])
                        curSub = product?.subscriptions[0];
                    setSelectedVariants([
                        {
                            ...(product.variants.find((vv) => vv.availableForSale == true) || product.variants[0]),
                            selling_plan:
                                product?.isSubscribed && curSub?.id
                                    ? curSub?.id
                                    : !product.oneTimePurchase
                                      ? product?.subscriptions &&
                                        product?.subscriptions.find((sub: any) => sub.checked === true)?.id
                                      : "",
                            ...(product?.isSubscribed && {
                                price:
                                    getFinalPrice(
                                        curSub?.discountType,
                                        curSub?.discountValue,
                                        +product.variants[0].price,
                                    ) + "",
                            }),
                            copyPrice: product.variants[0].price,
                        },
                    ]);

                    setFinalProductsList((prev: any) => {
                        const newArr = [...prev];
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const { images, options, variants, ...restt } = product;
                        newArr.push({
                            ...restt,
                            selectedVariants: [
                                {
                                    ...product.variants[0],
                                    selling_plan:
                                        product?.isSubscribed && curSub?.id
                                            ? curSub?.id
                                            : !product.oneTimePurchase
                                              ? product?.subscriptions &&
                                                product?.subscriptions.find((sub: any) => sub.checked === true)?.id
                                              : "",
                                    ...(product?.isSubscribed && {
                                        price:
                                            getFinalPrice(
                                                curSub?.discountType,
                                                curSub?.discountValue,
                                                +product.variants[0].price,
                                            ) + "",
                                    }),
                                    copyPrice: product.variants[0].price,
                                },
                            ],
                        });
                        return newArr;
                    });
                }}
            >
                {bundleConfig.add_button || "Add"}
            </button>
        </InspectorEditableBlock>
    );

    const renderQuantity = (
        <>
            <div className={styles.subInfo}>
                <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="quantityLabel">
                    <div
                        className={styles.qtyLabel}
                        style={{
                            color: bundleConfig?.bundles_design?.product_name_color,
                        }}
                    >
                        {bundleConfig.quantity_text || "Qty"}
                    </div>
                </InspectorEditableBlock>
                <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productQuantitySelector">
                    <div className={styles.qtyValue}>
                        <button
                            className={styles.quantityButton}
                            onClick={() => updateQuantity(-1)}
                            style={{
                                backgroundColor: bundleConfig?.bundles_design?.add_to_cart_bg_color,
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" style={{ height: 9, width: 9 }} viewBox="0 0 10 2">
                                <path
                                    fill="currentColor"
                                    fill-rule="evenodd"
                                    d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 1 1 0 1H1A.5.5 0 0 1 .5 1"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </button>
                        <span
                            style={{
                                minWidth: 20,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                fontWeight: "600",
                            }}
                        >
                            {selectedVariants?.length}
                        </span>
                        <button
                            className={styles.quantityButton}
                            onClick={() => updateQuantity(+1)}
                            style={{
                                backgroundColor: bundleConfig?.bundles_design?.add_to_cart_bg_color,
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" style={{ height: 9, width: 9 }} viewBox="0 0 10 10">
                                <path
                                    fill="currentColor"
                                    fill-rule="evenodd"
                                    d="M1 4.51a.5.5 0 0 0 0 1h3.5l.01 3.5a.5.5 0 0 0 1-.01V5.5l3.5-.01a.5.5 0 0 0-.01-1H5.5L5.49.99a.5.5 0 0 0-1 .01v3.5l-3.5.01z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </button>
                    </div>
                </InspectorEditableBlock>
            </div>
        </>
    );
    const userShopname = standAlone ? window.kefi_bundles.shop : shopify.config.shop;
    const effectiveMetafield = buildMetafieldFromSwatchOptions({
        product,
        bundleConfig: bundleConfig || {},
    });

    const isProductAvailable = useMemo(() => {
        return product.variants.some((vv) => (vv.quantityAvailable && vv.quantityAvailable > 0) || vv.availableForSale);
    }, [product]);

    return (
        <InspectorEditableBlock
            bundleType={bundleConfig?.bundle_type}
            settingKey="productCard"
            tagsKey={product.tags?.toString()}
            productId={getProductId(product.id)}
        >
            {/* {index > 0 && <span className={styles.separator}></span>} */}

            <div
                style={{
                    padding: "14px 12px",
                    borderTopLeftRadius: index == 0 ? bundleConfig?.bundles_design?.border_radius : "0px",
                    borderTopRightRadius: index == 0 ? bundleConfig?.bundles_design?.border_radius : "0px",
                    borderBottomLeftRadius:
                        index == bundleConfig?.products.length - 1
                            ? bundleConfig?.bundles_design?.border_radius
                            : "0px",
                    borderBottomRightRadius:
                        index == bundleConfig?.products.length - 1
                            ? bundleConfig?.bundles_design?.border_radius
                            : "0px",
                    // borderRadius: bundleConfig?.bundles_design?.border_radius,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                }}
                className={styles.productCard}
            >
                <div className={styles.productInfoRight}>
                    <div className={styles.productMetaLeft}>
                        <ProductImageWrapper
                            product={{
                                ...product,
                                quantity: selectedVariants.length,
                            }}
                            handleProductClick={handleProductClick}
                            bundleConfig={bundleConfig}
                            isMobile={isMobile}
                            selectedVariants={selectedVariants}
                        />
                        <div
                            style={{
                                fontSize:
                                    bundleConfig?.bundles_design.product_name_size < 50
                                        ? bundleConfig?.bundles_design.product_name_size
                                        : 50,
                                color: bundleConfig?.bundles_design?.product_name_color,
                                fontWeight: bundleConfig?.bundles_design?.product_name_style?.split(" ")[0],
                                fontStyle: bundleConfig?.bundles_design?.product_name_style?.split(" ")[1],
                                cursor: "pointer",
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 10,
                            }}
                        >
                            <div
                                onClick={handleProductClick}
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    width: "100%",
                                    gap: "10px",
                                }}
                                className={styles.productTitle_Price}
                            >
                                <InspectorEditableBlock
                                    bundleType={bundleConfig?.bundle_type}
                                    settingKey="productTitle"
                                    wrapWithDiv={true}
                                >
                                    <span>{product?.title}</span>
                                </InspectorEditableBlock>
                                <span style={{ whiteSpace: "nowrap" }}>{renderPrice()}</span>
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    width: "100%",
                                }}
                            >
                                {selectedVariants?.length > 0 ? (
                                    renderQuantity
                                ) : isProductAvailable ? (
                                    renderAddCta
                                ) : (
                                    <span
                                        style={{
                                            fontSize: "12px",
                                            color: "red",
                                        }}
                                    >
                                        {bundleConfig?.bundles_settings?.out_of_stock_text}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {product?.variants.length > 1 && (
                    <div
                        style={{
                            marginTop: selectedVariants?.length > 0 ? "10px" : "0px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                            width: "100%",
                        }}
                    >
                        {selectedVariants?.map((_: any, i: number) =>
                            userShopname === "typsy-beauty.myshopify.com" ? (
                                <Swatches
                                    bundlesDesign={bundleConfig?.bundles_design}
                                    key={i}
                                    variants={product.variants}
                                    handleChange={setSelectedVariants}
                                    selected={selectedVariants}
                                    options={product.options || []}
                                    productCardIndex={i}
                                    standAlone={standAlone}
                                />
                            ) : bundleConfig?.show_swatches && effectiveMetafield ? (
                                <CustomSwatches
                                    key={i}
                                    variants={product.variants}
                                    handleChange={setSelectedVariants}
                                    selected={selectedVariants}
                                    options={product.options || []}
                                    productCardIndex={i}
                                    standAlone={standAlone}
                                    metafield={effectiveMetafield}
                                    bundlesDesign={bundleConfig?.bundles_design}
                                    bundleConfig={bundleConfig}
                                />
                            ) : (
                                <VariantSelectorV2
                                    key={i}
                                    variants={product.variants}
                                    handleChange={setSelectedVariants}
                                    selected={selectedVariants}
                                    options={product.options || []}
                                    productCardIndex={i}
                                    standAlone={standAlone}
                                    metafield={effectiveMetafield}
                                    bundlesDesign={bundleConfig?.bundles_design}
                                    bundleConfig={bundleConfig}
                                />
                            ),
                        )}
                    </div>
                )}
            </div>
        </InspectorEditableBlock>
    );
};

export const BuyNPreview = ({ bundleConfig, standAlone, appConfigContext }: Props_Buy_N_Preview) => {
    const [totalPrice, setTotalPrice] = useState(0);
    const { currency, money_format, sft } = appConfigContext;
    const [totalQuantities, setTotalQuantities] = useState<number>(0);
    const [discountType, setDiscountType] = useState<"percentage" | "flat" | "free" | "fixed">("percentage");
    const [discountValue, setDiscountValue] = useState<number>(0);

    // 🆕 updated state to handle both products and page info
    const [collectionBasedProducts, setCollectionBasedProducts] = useState<ProductObj[]>([]);
    const [loadingMore, setLoadingMore] = useState(false);
    const [, setPage] = useState(1);
    const [pageInfo, setPageInfo] = useState<any>(null); // 🆕 state to store page info
    const fetchCount = 5;

    const bundleRef = useRef<HTMLDivElement>(null);
    const { isMobile } = useIsMobile();
    const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);
    const [unavailableSelected, setUnavailableSelected] = useState<{
        [key: number]: number[];
    }>({});

    const loadMoreCollectionProducts = async () => {
        if (loadingMore) return; // Prevent multiple fetches

        const gid = bundleConfig.collections[0]?.id;
        const id = gid?.split("/").pop() || "";

        const { products, pageInfo: newPageInfo } = await fetchCollectionProducts(
            id,
            sft,
            setLoadingMore,
            standAlone,
            fetchCount,
            pageInfo?.endCursor || null,
        );

        setCollectionBasedProducts((prevProducts) => [...prevProducts, ...products]);
        setPageInfo(newPageInfo);
        setLoadingMore(false);
    };

    useEffect(() => {
        if (bundleConfig?.product_selection_type !== "collection" || bundleConfig?.collections?.length == 0) {
            setCollectionBasedProducts([]);
            setPageInfo(null);
            setLoadingMore(false);
            setPage(1);
            return;
        }

        loadMoreCollectionProducts();
    }, [bundleConfig?.collections, bundleConfig?.product_selection_type, bundleConfig?.render_product_pages_type]);

    useEffect(() => {
        const trigger = loadMoreTriggerRef.current;
        const scrollContainer = bundleRef.current;

        if (!trigger || !scrollContainer || !pageInfo?.hasNextPage) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loadingMore) {
                    observer.disconnect();
                    loadMoreCollectionProducts();
                }
            },
            {
                root: scrollContainer,
                threshold: 0.2,
            },
        );

        observer.observe(trigger); // observe latest trigger

        return () => {
            observer.unobserve(trigger); // cleanup previous trigger
            observer.disconnect();
        };
    }, [pageInfo?.hasNextPage, loadMoreTriggerRef, collectionBasedProducts.length]); // 🆕 observe new trigger on update

    useEffect(() => {
        if (collectionBasedProducts?.length) {
            window?.localStorage.setItem("buy_n_handle", collectionBasedProducts[0]?.handle);
        }
    }, [collectionBasedProducts]);

    const getDiscountOption = () => {
        return bundleConfig?.discount_options.find((option) => {
            const isWithinQuantityRange = totalQuantities == option.required_item_count;
            return isWithinQuantityRange;
        });
    };

    useEffect(() => {
        const discountOption = getDiscountOption();

        if (discountOption) {
            setDiscountType(discountOption.discount_type);
            setDiscountValue(discountOption.discount_value || 0);
        } else {
            setDiscountType("percentage");
            setDiscountValue(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalQuantities, bundleConfig?.discount_options, bundleConfig?.products]);

    const [finalProductsList, setFinalProductsList] = useState<ProductObj[]>([]);

    useEffect(() => {
        setFinalProductsList([]);
    }, [bundleConfig?.product_selection_type]);

    const updatedBundleConfig = {
        ...bundleConfig,
        products: [...finalProductsList],
        // discount_type: discountType,
        // discount_value: discountValue,
    };

    const [allStoreProducts, setAllStoreProducts] = useState<any[]>([]);
    const [loadingAllProducts, setLoadingAllProducts] = useState<boolean>(false);

    useEffect(() => {
        const getAllProducts = async () => {
            if (bundleConfig?.product_selection_type === "all") {
                setLoadingAllProducts(true);
                try {
                    const products = await fetchStoreProducts(sft, standAlone, 100);
                    setAllStoreProducts(products || []);
                } catch (error) {
                    console.error("Error fetching all products:", error);
                } finally {
                    setLoadingAllProducts(false);
                }
            }
        };

        getAllProducts();
    }, [bundleConfig?.product_selection_type]);

    const findDiscountProducts = () => {
        const discount_option_type = bundleConfig?.discount_option_type;
        const discountOption = getDiscountOption();
        if (!discountOption) {
            return [];
        }

        const discountedProductsCount = discountOption?.discounted_item_count;

        const allSelectedVariants = finalProductsList.map((fp) => fp?.selectedVariants).flat();
        const sortedVariants = allSelectedVariants.sort((a: any, b: any) => {
            if (discount_option_type === "cheapest") {
                return Number(a.price) - Number(b.price);
            } else if (discount_option_type === "expensive") {
                return Number(b.price) - Number(a.price);
            }
            return 0;
        });

        const result = sortedVariants.slice(0, discountedProductsCount);
        return result;
    };

    const [discountedVariantsArray, setDiscountedVariantsArray] = useState<any[]>([]);

    useEffect(() => {
        setDiscountedVariantsArray(findDiscountProducts());
    }, [finalProductsList, bundleConfig?.discount_option_type, bundleConfig?.discount_options]);

    const [priceSummary, setPriceSummary] = useState({
        totalOriginal: 0,
        totalDiscounted: 0,
    });
    const [appliedDiscountOptions, setAppliedDiscountOptions] = useState<Set<number>>(new Set());
    useEffect(() => {
        const calculateTotalPrices = () => {
            let totalOriginal = 0;
            let totalDiscounted = 0;

            const allSelectedVariants = [...finalProductsList.flatMap((p) => p.selectedVariants || [])];
            allSelectedVariants.forEach((variant: any) => {
                totalOriginal += parseFloat(variant.price);
            });

            if (bundleConfig?.discount_option_type === "cheapest") {
                allSelectedVariants.sort((a, b) => {
                    return parseFloat(b.price) - parseFloat(a.price);
                });
            } else if (bundleConfig?.discount_option_type === "expensive") {
                allSelectedVariants.sort((a, b) => {
                    return parseFloat(a.price) - parseFloat(b.price);
                });
            }

            const discounts: any[] = [];

            let totalQtyAvailable: number = allSelectedVariants.length;
            let startIndex: number = 0;
            let endIndex: number = totalQtyAvailable - 1;

            // Count how many discounts to apply
            const _do = [...bundleConfig.discount_options];
            _do.sort((a, b) => {
                return b?.required_item_count - a?.required_item_count;
            });
            const appliedOptions = new Set<number>();
            _do.forEach((option: any) => {
                const applicableDiscountRule: any = option;

                while (totalQtyAvailable >= applicableDiscountRule.required_item_count) {
                    appliedOptions.add(applicableDiscountRule.required_item_count);
                    const targetsForDiscount = allSelectedVariants.slice(
                        endIndex + 1 - applicableDiscountRule.discounted_item_count,
                        endIndex + 1,
                    );

                    discounts.push({
                        targets: targetsForDiscount,
                        value: getDiscountValue(
                            applicableDiscountRule.discount_type,
                            applicableDiscountRule.discount_value,
                            targetsForDiscount,
                        ),
                    });

                    totalQtyAvailable -= applicableDiscountRule.required_item_count;
                    endIndex -= applicableDiscountRule.discounted_item_count;
                    startIndex +=
                        applicableDiscountRule.required_item_count - applicableDiscountRule.discounted_item_count;
                }
            });
            setAppliedDiscountOptions(appliedOptions);

            let totalDiscount = 0;

            discounts.forEach((discount: any) => {
                const { targets, value } = discount;

                let targetTotal = 0;
                targets.forEach((t: any) => {
                    targetTotal += parseFloat(t.price);
                });

                if (value?.percentage) {
                    totalDiscount += (targetTotal * value.percentage.value) / 100;
                }

                if (value?.fixedAmount) {
                    totalDiscount += value.fixedAmount.amount;
                }
            });

            // 3. Final discounted price
            totalDiscounted = totalOriginal - totalDiscount;
            if (totalDiscounted < 0) totalDiscounted = 0;

            setPriceSummary({
                totalOriginal,
                totalDiscounted,
            });
        };

        calculateTotalPrices();
    }, [finalProductsList, discountedVariantsArray, discountType, discountValue, bundleConfig?.discount_option_type]);

    if (!bundleConfig) return null;

    return (
        <BundlePreviewWrapper
            // @ts-expect-error required
            bundleConfig={updatedBundleConfig}
            standAlone={standAlone}
            totalPrice={totalPrice - priceSummary.totalDiscounted}
            currency={currency}
            money_format={money_format}
            discountedPrice={priceSummary.totalDiscounted}
        >
            <>
                {/* Top tabs for quantity ranges */}
                {(bundleConfig?.products?.length > 0 ||
                    (bundleConfig?.product_selection_type == "collection" && collectionBasedProducts.length > 0) ||
                    (bundleConfig?.product_selection_type == "all" && allStoreProducts?.length > 0)) && (
                    <div
                        className={styles.quantityTabs}
                        style={{
                            backgroundColor: bundleConfig?.bundles_design?.discount_tabs_bg_color,
                            borderRadius: bundleConfig?.bundles_design?.discount_tabs_border_radius,
                        }}
                    >
                        {bundleConfig?.discount_options?.map((option) => {
                            const isActive = appliedDiscountOptions.has(option.required_item_count);

                            return (
                                <>
                                    <InspectorEditableBlock
                                        bundleType={bundleConfig?.bundle_type}
                                        settingKey="discountOption"
                                    >
                                        <div
                                            className={`${styles.discountOption} ${isActive ? styles.active : ""}`}
                                            style={{
                                                color: isActive
                                                    ? "green"
                                                    : bundleConfig?.bundles_design?.discount_tabs_discount_label_color,
                                                display: "flex",
                                                flexDirection: "row",
                                                gap: "10px",
                                                alignItems: "center",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                {isActive ? (
                                                    <TickIconSvg color={"green"} />
                                                ) : (
                                                    <DottedCircle
                                                        color={
                                                            bundleConfig?.bundles_design
                                                                ?.discount_tabs_discount_label_color || "#000000"
                                                        }
                                                    />
                                                )}
                                            </span>
                                            <p
                                                className={isActive ? styles.active_label : ""}
                                                style={{
                                                    fontSize:
                                                        bundleConfig?.bundles_design?.discount_tabs_quantity_label_size,
                                                    fontWeight:
                                                        bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                            " ",
                                                        )[0],
                                                    fontStyle:
                                                        bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                            " ",
                                                        )[1],
                                                    margin: 0,
                                                    lineHeight: "normal",
                                                }}
                                            >
                                                {option?.label_text}
                                            </p>
                                        </div>
                                    </InspectorEditableBlock>
                                </>
                            );
                        })}
                    </div>
                )}
                <div
                    className={styles.products}
                    ref={bundleRef}
                    style={{
                        borderRadius: bundleConfig?.bundles_design?.border_radius,
                        border: `${bundleConfig?.bundles_design?.border_width}px solid ${bundleConfig?.bundles_design?.product_border_color}`,
                        backgroundColor: bundleConfig?.bundles_design?.product_background_color,
                        maxHeight: "450px",
                        overflow: "auto",
                        WebkitOverflowScrolling: "auto",
                        overscrollBehavior: "auto",
                    }}
                >
                    {bundleConfig?.product_selection_type === "all" ? (
                        <>
                            {loadingAllProducts ? (
                                <ProductCardShimmer type="specific" />
                            ) : allStoreProducts.length > 0 ? (
                                allStoreProducts.map((product, index) => (
                                    <ProductCard
                                        key={product?.id + product?.variants[0]?.id}
                                        index={index}
                                        product={product}
                                        bundleConfig={bundleConfig}
                                        appConfigContext={appConfigContext}
                                        standAlone={standAlone || false}
                                        setTotalQuantities={setTotalQuantities}
                                        discountType={discountType}
                                        discountValue={discountValue}
                                        onVariantChange={() => {}}
                                        unavailableSelected={unavailableSelected[index] || []}
                                        setUnavailableSelected={setUnavailableSelected}
                                        finalProductsList={finalProductsList}
                                        setFinalProductsList={setFinalProductsList}
                                        setTotalPrice={setTotalPrice}
                                        isMobile={isMobile}
                                        discountedVariantsArray={discountedVariantsArray}
                                    />
                                ))
                            ) : (
                                <div style={{ padding: "10px", color: "#696969" }}>No products available.</div>
                            )}
                        </>
                    ) : null}

                    {bundleConfig?.product_selection_type === "all" ? null : bundleConfig?.product_selection_type ===
                          "collection" && collectionBasedProducts.length > 0 ? (
                        <>
                            {collectionBasedProducts.map((product, index) => (
                                <ProductCard
                                    key={product?.id + product?.variants[0]?.id}
                                    index={index}
                                    product={product}
                                    bundleConfig={bundleConfig}
                                    appConfigContext={appConfigContext}
                                    standAlone={standAlone || false}
                                    setTotalQuantities={setTotalQuantities}
                                    discountType={discountType}
                                    discountValue={discountValue}
                                    onVariantChange={() => {}}
                                    unavailableSelected={unavailableSelected[index] || []}
                                    setUnavailableSelected={setUnavailableSelected}
                                    finalProductsList={finalProductsList}
                                    setFinalProductsList={setFinalProductsList}
                                    setTotalPrice={setTotalPrice}
                                    isMobile={isMobile}
                                    type="collection"
                                    discountedVariantsArray={discountedVariantsArray}
                                />
                            ))}
                        </>
                    ) : bundleConfig?.products?.length ? (
                        <>
                            {bundleConfig.products.map((product, index) => (
                                <ProductCard
                                    key={product?.id + product?.variants[0]?.id}
                                    index={index}
                                    product={product}
                                    bundleConfig={bundleConfig}
                                    appConfigContext={appConfigContext}
                                    standAlone={standAlone || false}
                                    setTotalQuantities={setTotalQuantities}
                                    discountType={discountType}
                                    discountValue={discountValue}
                                    onVariantChange={() => {}}
                                    unavailableSelected={unavailableSelected[index] || []}
                                    setUnavailableSelected={setUnavailableSelected}
                                    finalProductsList={finalProductsList}
                                    setFinalProductsList={setFinalProductsList}
                                    setTotalPrice={setTotalPrice}
                                    isMobile={isMobile}
                                    discountedVariantsArray={discountedVariantsArray}
                                />
                            ))}
                        </>
                    ) : (
                        <ProductCardShimmer
                            type={bundleConfig?.product_selection_type === "collection" ? "collection" : "specific"}
                        />
                    )}
                    <div className={styles.loadMoreWrapper}>
                        {loadingMore ? (
                            <div
                                style={{
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignContent: "center",
                                    padding: 10,
                                }}
                            >
                                <Spinner color={"#939393"} size={50} />
                                <span
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        fontSize: 12,
                                        color: "#939393",
                                    }}
                                >
                                    Loading more products...
                                </span>
                            </div>
                        ) : (
                            collectionBasedProducts.length > 0 &&
                            pageInfo?.hasNextPage && (
                                <span
                                    ref={loadMoreTriggerRef}
                                    style={{ width: "100%", display: "flex", height: "10px", visibility: "hidden" }}
                                />
                            )
                        )}
                    </div>
                </div>
            </>
        </BundlePreviewWrapper>
    );
};
export default BuyNbundle;
